import { alpha, Box, List, Typography } from "@mui/material";
import useDictionaries from "features/dictionaries/useDictionaries";
import { ProgramItem } from "features/programs/programApi";
import moment from "moment";
import { useMemo } from "react";
import { CateringItem, Summary } from "./cateringApi";
import CateringListItem from "./CateringListItem";
import OrderItem from "./OrderItem";

interface CateringListProps {
  cateringData: CateringItem[];
}

const summaryColors = {
  option: "lightblue",
  variant: "lightgreen",
};

function getNow() {
  return moment(); //.add(7, "hours");
}

export default function CateringList({ cateringData }: CateringListProps) {
  const { resolveOption } = useDictionaries();

  // Meghatározzuk a státuszokat az összes catering elemhez
  const cateringWithStatus = useMemo(() => {
    const now = getNow();

    // Megkeressük a következő induló időpontot (legkorábbi jövőbeli, de csak a mai napon)
    const futureItems = cateringData
      .filter(item => {
        const startTime = moment(item.start_time);
        return startTime.isAfter(now) && startTime.isSame(now, "day"); // csak a mai napon
      })
      .sort((a, b) => moment(a.start_time).diff(moment(b.start_time)));

    if (futureItems.length === 0) {
      // Nincs jövőbeli járat, csak a folyamatban lévőket jelöljük
      return cateringData.map(item => {
        const startTime = moment(item.start_time);
        const maxEndTime = item.program_ids.reduce((latest, programId) => {
          const program = resolveOption<ProgramItem>("programs", programId);
          const endTime = moment(startTime).add(
            program?.duration || 60,
            "minutes"
          );
          return endTime.isAfter(latest) ? endTime : latest;
        }, startTime);

        const isRunning = now.isBetween(startTime, maxEndTime);
        return {
          ...item,
          status: isRunning ? "running" : null,
        };
      });
    }

    const nextStartTime = moment(futureItems[0].start_time);
    const thirtyMinutesLater = moment(nextStartTime).add(30, "minutes");

    return cateringData.map(item => {
      const startTime = moment(item.start_time);
      const maxEndTime = item.program_ids.reduce((latest, programId) => {
        const program = resolveOption<ProgramItem>("programs", programId);
        const endTime = moment(startTime).add(
          program?.duration || 60,
          "minutes"
        );
        return endTime.isAfter(latest) ? endTime : latest;
      }, startTime);

      // Folyamatban van?
      const isRunning = now.isBetween(startTime, maxEndTime);

      // Befejezett?
      const isFinished = maxEndTime.isBefore(now);

      // Következő? (pontosan a legközelebbi induló időpont, de csak a mai napon)
      const isNext =
        !isFinished &&
        !isRunning &&
        startTime.isSame(nextStartTime) &&
        startTime.isSame(now, "day"); // csak a mai napon lehet "következő"

      // Hamarosan indul? (a következő első indulás és a +30 perc között, de nem a következő, és csak a mai napon)
      const isUpcoming =
        !isFinished &&
        !isRunning &&
        !isNext &&
        startTime.isSame(now, "day") && // csak a mai napon
        startTime.isBetween(nextStartTime, thirtyMinutesLater, null, "[]");

      let status = null;
      if (isFinished) {
        status = "finished";
      } else if (isRunning) {
        status = "running";
      } else if (isNext) {
        status = "next";
      } else if (isUpcoming) {
        status = "upcoming";
      }

      return {
        ...item,
        status,
      };
    });
  }, [cateringData, resolveOption]);

  // Rendezzük a catering adatokat: aktív/közelgő programok elöl, elmúltak hátul
  const sortedCateringData = useMemo(() => {
    const now = getNow();

    return [...cateringWithStatus].sort((a, b) => {
      const aStartTime = moment(a.start_time);
      const bStartTime = moment(b.start_time);

      // Kiszámítjuk mindkét program legkésőbbi végét
      const aMaxEndTime = a.program_ids.reduce((latest, programId) => {
        const program = resolveOption<ProgramItem>("programs", programId);
        const endTime = moment(aStartTime).add(
          program?.duration || 60,
          "minutes"
        );
        return endTime.isAfter(latest) ? endTime : latest;
      }, aStartTime);

      const bMaxEndTime = b.program_ids.reduce((latest, programId) => {
        const program = resolveOption<ProgramItem>("programs", programId);
        const endTime = moment(bStartTime).add(
          program?.duration || 60,
          "minutes"
        );
        return endTime.isAfter(latest) ? endTime : latest;
      }, bStartTime);

      // Kategorizáljuk a programokat
      const aIsFinished = aMaxEndTime.isBefore(now);
      const bIsFinished = bMaxEndTime.isBefore(now);

      // Ha az egyik befejezett, a másik nem
      if (aIsFinished && !bIsFinished) return 1; // a hátrébb
      if (!aIsFinished && bIsFinished) return -1; // b hátrébb

      // Ha mindkettő ugyanabban a kategóriában, időrend szerint rendezünk
      return aStartTime.diff(bStartTime);
    });
  }, [cateringWithStatus, resolveOption]);

  // Group and sum the summaries by name
  const groupedSummaries = sortedCateringData
    .flatMap(item => item.summary)
    .reduce((acc, summary) => {
      const existing = acc.find(s => s.name === summary.name);
      if (existing) {
        existing.qty += summary.qty;
      } else {
        acc.push({ ...summary });
      }
      return acc;
    }, [] as Summary[]);
  groupedSummaries.sort((a, b) => b.qty - a.qty);

  const goupedGuestSummary = useMemo(() => {
    let sum = {
      number_of_guests: 0,
      number_of_adults: 0,
      number_of_kids: 0,
      number_of_babies: 0,
      number_of_guides: 0,
      guests_arrived: 0,
      bookings_arrived: 0,
      bookings_armbanded: 0,
      guests_armbanded: 0,
      number_of_bookings: 0,
      tables_reserved: 0,
      tables_left: 0,
      bookings_unpaid: 0,
    };
    sortedCateringData.forEach(item => {
      sum.number_of_guests += item.guestSummary.total.number_of_guests || 0;
      sum.number_of_adults += item.guestSummary.total.number_of_adults || 0;
      sum.number_of_kids += item.guestSummary.total.number_of_kids || 0;
      sum.number_of_babies += item.guestSummary.total.number_of_babies || 0;
      sum.number_of_guides += item.guestSummary.total.number_of_guides || 0;
      sum.guests_arrived += item.guestSummary.total.guests_arrived || 0;
      sum.bookings_arrived += item.guestSummary.total.bookings_arrived || 0;
      sum.guests_armbanded += item.guestSummary.total.guests_armbanded || 0;
      sum.bookings_armbanded += item.guestSummary.total.bookings_armbanded || 0;
      sum.number_of_bookings += item.guestSummary.total.number_of_bookings || 0;
      sum.tables_reserved += item.guestSummary.total.tables_reserved || 0;
      sum.tables_left += item.guestSummary.total.tables_left || 0;
      sum.bookings_unpaid += item.guestSummary.total.bookings_unpaid || 0;
    });
    return sum;
  }, [sortedCateringData]);
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {sortedCateringData.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1,
            flexWrap: "wrap",
            p: 1,
            borderRadius: "6px",
            border: theme => `solid 1px ${alpha(theme.palette.info.main, 0.4)}`,
            backgroundColor: theme => alpha(theme.palette.info.main, 0.1),
          }}
        >
          <Typography variant="body1" align="center">
            Összesítés
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "row",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <OrderItem
              name="Vendégek"
              qty={goupedGuestSummary.number_of_guests}
              type="option"
            />
            <OrderItem
              name="Foglalások"
              qty={goupedGuestSummary.number_of_bookings}
              type="option"
            />
            <OrderItem
              name="Megérkezett"
              qty={goupedGuestSummary.guests_arrived}
              type="option"
            />
            <OrderItem
              name="Karszalag"
              qty={goupedGuestSummary.guests_armbanded}
              type="option"
            />
            <OrderItem
              name="Leültetett"
              qty={goupedGuestSummary.tables_reserved}
              type="option"
            />
          </Box>
        </Box>
      )}
      <List disablePadding dense>
        {sortedCateringData.map(item => (
          <CateringListItem
            key={`${item.boat_id}-${item.start_time}`}
            item={item}
            status={item.status}
          />
        ))}
      </List>
    </Box>
  );
}
